import type { ReactNode } from 'react';
import { useEffect, useId } from 'react';
import { Botao } from './Botao.js';
import type { VarianteBotao } from './Botao.js';
import './DialogoConfirmacao.css';

type Props = {
  /** A pergunta, com o alvo por extenso: "Bloquear o coletor COLETOR-DOCA-1?" */
  readonly titulo: string;
  /** A consequência, em texto. É o que a pessoa precisa ler antes de decidir. */
  readonly children: ReactNode;
  readonly rotuloConfirmar: string;
  readonly rotuloCancelar?: string;
  readonly aoConfirmar: () => void;
  readonly aoCancelar: () => void;
  /** `destrutivo` por padrão — este diálogo existe para o que não se desfaz. */
  readonly variante?: VarianteBotao;
  /** Trava a confirmação enquanto a ação corre. */
  readonly agindo?: boolean;
  /** Trava só a confirmação — falta um dado obrigatório (o motivo, por exemplo). */
  readonly confirmacaoBloqueada?: boolean;
};

/**
 * Confirmação de ação destrutiva.
 *
 * `alertdialog` e não `dialog`: o leitor de tela anuncia a consequência junto
 * com o título, em vez de esperar que a pessoa navegue até o texto. O foco
 * nasce em **Cancelar** — a saída segura é o padrão, e um Enter apressado não
 * bloqueia um coletor com fila pendente (UC12 2a).
 */
export function DialogoConfirmacao({
  titulo,
  children,
  rotuloConfirmar,
  rotuloCancelar = 'Cancelar',
  aoConfirmar,
  aoCancelar,
  variante = 'destrutivo',
  agindo = false,
  confirmacaoBloqueada = false,
}: Props) {
  const idTitulo = useId();
  const idDescricao = useId();

  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') {
        aoCancelar();
      }
    }

    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aoCancelar]);

  return (
    <div className="dialogo">
      <div
        className="dialogo__veu"
        role="presentation"
        onClick={() => {
          aoCancelar();
        }}
      />
      <div
        className="dialogo__caixa"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-describedby={idDescricao}
      >
        <h2 className="dialogo__titulo" id={idTitulo}>
          {titulo}
        </h2>
        <div className="dialogo__corpo" id={idDescricao}>
          {children}
        </div>
        <div className="dialogo__acoes">
          {/* O foco nasce aqui: a saída segura é o padrão. */}
          <Botao autoFocus variante="secundario" onClick={aoCancelar} disabled={agindo}>
            {rotuloCancelar}
          </Botao>
          <Botao
            variante={variante}
            onClick={aoConfirmar}
            disabled={agindo || confirmacaoBloqueada}
          >
            {rotuloConfirmar}
          </Botao>
        </div>
      </div>
    </div>
  );
}
